/**
 * Build the create/PATCH payload of a recurring schedule from the
 * ScheduleEditor form values.
 *
 * On EDIT an emptied optional field must reach the backend as `null`:
 * `undefined` is dropped by JSON.stringify, and PATCH (rightly) treats an
 * absent key as "leave it alone" — so a cleared pause window / end date /
 * delivery window silently kept its old value (Seb, Sep 7 2026). On CREATE
 * `undefined` is fine (the row starts empty).
 */
export function buildScheduleDto(values, { editing = false } = {}) {
  const cleared = editing ? null : undefined;
  return {
    pickupSiteId: values.pickupSiteId ?? cleared,
    deliverySiteId: values.deliverySiteId ?? cleared,
    // #170 / #169 — the task's box and its stop order in the tournée.
    collectionPointId: values.collectionPointId ?? cleared,
    position: values.position ?? cleared,
    weekdays: values.weekdays,
    startDate: values.startDate.format('YYYY-MM-DD'),
    endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : cleared,
    deliveryTimeStart: values.deliveryTimeStart || cleared,
    deliveryTimeEnd: values.deliveryTimeEnd || cleared,
    deliveryDuration: values.deliveryDuration ?? cleared,
    defaultLoad: values.defaultLoad ?? cleared,
    defaultWeight: values.defaultWeight ?? cleared,
    defaultVolume: values.defaultVolume ?? cleared,
    preparationMode: values.preparationMode || cleared,
    // OVO-223 — always send the (possibly empty) array so clearing every
    // tag on edit actually wipes the schedule's types. Stamped onto each
    // materialized order's `types` (skills/tour matching).
    defaultTypes: values.defaultTypes || [],
    // OVO-147 — always send the (possibly empty) array so removing
    // every row on edit actually clears the schedule's products.
    defaultProducts: (values.defaultProducts || [])
      .filter((p) => p && p.partnerItemTypeId)
      .map((p) => ({
        partnerItemTypeId: p.partnerItemTypeId,
        quantity: p.quantity ?? 1,
        weight: p.weight ?? undefined,
      })),
    isActive: values.isActive,
    pausedFrom: values.pausedFrom ? values.pausedFrom.format('YYYY-MM-DD') : cleared,
    pausedUntil: values.pausedUntil ? values.pausedUntil.format('YYYY-MM-DD') : cleared,
    notes: values.notes || cleared,
  };
}
